
/**
 * @fileoverview Hook de Form Layer para la gestión y validación de tags en el formulario de CustomerDetailMC.
 * Proporciona handlers para crear tags locales, guardar tags del cliente y exponer el estado y validaciones del formulario.
 * @module useCustomerDetailTagsForm
 * @category Hook - Form Layer
 * @since 1.0.0
 * @version 2.1.0
 */

import { useCallback, useMemo } from 'react';

/**
 * @typedef {Object} TagsData
 * @property {string[]} localCreatedTags - Tags creados localmente
 * @property {function} setLocalCreatedTags - Setter para tags locales
 * @property {function} checkTagExists - Verifica si el tag existe
 *
 * @typedef {Object} TagsActions
 * @property {function} createLocalTag - Crea un tag local
 * @property {function} saveCustomerTags - Guarda los tags del cliente
 * @property {boolean} loading - Estado de carga de acciones
 *
 * @typedef {Object} TagsUI
 * @property {string[]} selectedTags - Tags seleccionados
 * @property {boolean} hasChanges - Indica si hay cambios en el formulario
 *
 * @typedef {Object} FormState
 * @property {number} selectedTagsCount - Cantidad de tags seleccionados
 * @property {number} localCreatedTagsCount - Cantidad de tags locales
 * @property {boolean} hasChanges - Si hay cambios
 * @property {boolean} canSave - Si se puede guardar
 * @property {boolean} isLoading - Si está cargando
 *
 * @typedef {Object} UseCustomerDetailTagsFormReturn
 * @property {function} handleCreateTag - Handler para crear tag local
 * @property {function} handleSaveCustomerTags - Handler para guardar tags del cliente
 * @property {boolean} canSave - Si se puede guardar
 * @property {FormState} formState - Estado del formulario
 */

/**
 * Hook de Form Layer para la gestión y validación de tags en el formulario de CustomerDetailMC.
 *
 * Implementa el patrón de Form Layer con separación estricta de responsabilidades:
 * - Solo gestiona estado, validación y handlers del formulario de tags
 * - No realiza llamadas a API ni lógica de UI
 * - Optimizado con useCallback y useMemo para performance
 *
 * Flujo de operación:
 * 1. Expone handlers para crear y guardar tags
 * 2. Expone estado y validaciones del formulario
 *
 * @function useCustomerDetailTagsForm
 * @category Hook - Form Layer
 * @param {TagsData} tagsData - Datos y setters de tags locales
 * @param {TagsActions} tagsActions - Acciones para crear/guardar tags
 * @param {TagsUI} tagsUI - Estado UI del formulario de tags
 * @returns {UseCustomerDetailTagsFormReturn} Handlers y estado del formulario
 * @since 1.0.0
 * @version 2.1.0 - Optimización de performance y documentación
 */
const useCustomerDetailTagsForm = (tagsData, tagsActions, tagsUI) => {
  const {
    localCreatedTags,
    setLocalCreatedTags,
    checkTagExists
  } = tagsData;

  const {
    createLocalTag,
    saveCustomerTags
  } = tagsActions;

  const {
    selectedTags,
    hasChanges,
  } = tagsUI;

  /**
   * Handler para crear un tag local en el formulario.
   *
   * @memberof useCustomerDetailTagsForm
   * @async
   * @function handleCreateTag
   * @param {string} tagName - Nombre del tag a crear
   * @param {Object} [options]
   * @param {function} [options.checkTagExistsArg] - Función para verificar existencia
   * @param {function} [options.setLocalCreatedTagsArg] - Setter para tags locales
   * @returns {Promise<any>} Tag creado
   * @throws {Error} Si ocurre un error al crear el tag
   */
  const handleCreateTag = useCallback(async (
    tagName,
    {
      checkTagExistsArg = checkTagExists,
      setLocalCreatedTagsArg = setLocalCreatedTags
    } = {}
  ) => {
    try {
      return await createLocalTag(tagName, {
        checkTagExists: checkTagExistsArg,
        setLocalCreatedTags: setLocalCreatedTagsArg
      });
    } catch (error) {
      console.error('Error creando tag local:', error);
      throw error;
    }
  }, [createLocalTag]);

  /**
   * Handler para guardar los tags del cliente desde el formulario.
   *
   * @memberof useCustomerDetailTagsForm
   * @async
   * @function handleSaveCustomerTags
   * @param {Object} params - Parámetros para guardar tags
   * @param {string|number} params.customerId - ID del cliente
   * @param {string} params.platform - Plataforma
   * @param {string[]} params.selectedTagsArg - Tags seleccionados
   * @param {any[]} params.tagOptionsArg - Opciones de tags
   * @param {boolean} params.hasChangesArg - Si hay cambios
   * @param {function} params.refreshTagsArg - Refresca tags
   * @param {function} params.exitEditModeArg - Sale del modo edición
   * @param {function} params.setLocalCreatedTagsArg - Setter para tags locales
   * @returns {Promise<Object>} Resultado de la operación
   * @throws {Error} Si ocurre un error al guardar los tags
   */
  const handleSaveCustomerTags = useCallback(async ({
    customerId,
    platform,
    selectedTagsArg,
    tagOptionsArg,
    hasChangesArg,
    refreshTagsArg,
    exitEditModeArg,
    setLocalCreatedTagsArg
  }) => {
    if (!hasChangesArg) {
      exitEditModeArg();
      return { success: false, error: 'No changes to save' };
    }

    try {
      const result = await saveCustomerTags(selectedTagsArg, {
        tagOptions: tagOptionsArg,
        customerId,
        platform,
        refreshTags: refreshTagsArg,
        onSuccess: () => {
          exitEditModeArg();
        },
        onError: () => { },
        setLocalCreatedTags: setLocalCreatedTagsArg
      });

      return result;
    } catch (error) {
      return { success: false, error: error.message || error };
    }
  }, [saveCustomerTags]);

  const canSave = useMemo(() => {
    return hasChanges && !tagsActions.loading;
  }, [hasChanges, tagsActions.loading]);

  const formState = useMemo(() => ({
    selectedTagsCount: selectedTags.length,
    localCreatedTagsCount: localCreatedTags.length,
    hasChanges,
    canSave,
    isLoading: tagsActions.loading
  }), [
    selectedTags.length,
    localCreatedTags.length,
    hasChanges,
    canSave,
    tagsActions.loading
  ]);

  return useMemo(() => ({
    // Form handlers
    handleCreateTag,
    handleSaveCustomerTags,
    // Form validation
    canSave,
    // Form state
    formState
  }), [
    handleCreateTag,
    handleSaveCustomerTags,
    canSave,
    formState
  ]);
};

export default useCustomerDetailTagsForm;